import { useState } from 'react';
import { classNames } from '../../../../../utils/classNames';
import Table from '../../../../../shared/table';
import {
    useGetAllCallsQuery,
    useGetAllSearchValuesQuery,
} from '../../../../../store/excecutive/executiveApi';
import { IPage } from '../../../../../interfaces/models/models/paginated-response.model';
import { Controller, useForm } from 'react-hook-form';
import Select from '../../../../../shared/select';
import Input from '../../../../../shared/input';
import DateRangePopover from '../DateRange';
import CallLogModal from './modal/CallLogModal';
import DetailedTab from '../../tabs/DetailedTab';
import CallManagement from './CallManagement';

const DrillDetails = ({
    showFilters = true,
    changeTab,
}: {
    showFilters?: boolean;
    changeTab?: (value: any) => void;
}) => {
    const { register, control, reset, watch, setValue } = useForm();

    const handleReset = () => reset();

    const data = watch();

    const [tabs, setTabs] = useState<
        'all' | 'transfer' | 'success' | 'fail' | 'abandoned'
    >('all');
    const handleTabs = (tab: 'all' | 'transfer' | 'success' | 'fail' | 'abandoned') => {
        setTabs(tab);
        if (tab === 'all') setValue('finalStatus', '');
        else if (tab === 'abandoned') setValue('finalStatus', 'abandonment');
        else setValue('finalStatus', tab);
    };

    const COLUMNS_DETAILS = [
        'Call ID',
        'Customer Phone',
        'Ended Reason',
        'Final Status',
        'Call Details',
    ];

    const [pagination, setPagination] = useState<IPage>({
        number: 1,
        size: 6,
        totalElements: 1,
        totalPages: 1,
        hasNextPage: false,
    });
    const [show, setShow] = useState<{ show: boolean; callId: string }>();
    const { data: calls } = useGetAllCallsQuery({ ...pagination, ...data });

    const { data: searchValues } = useGetAllSearchValuesQuery();

    const statusOptions =
        searchValues?.data[0].finalStatus.map((value) => {
            if (value === 'abandonment') {
                return { label: 'abandoned', value };
            }
            if (value === 'transfer' || value === 'transferred') {
                return { label: 'Transferred', value: 'transfer' };
            }
            return { label: value, value };
        }) || [];
    const reasonsOptions =
        searchValues?.data[0].endedReason.map((value) => ({
            label: value,
            value,
        })) || [];

    const handleShow = (callId?: string) => {
        setShow(
            show?.show
                ? { show: false, callId: '' }
                : { show: true, callId: callId || '' },
        );
    };

    const formatStatus = (reason: string) => {
        switch (reason) {
            case 'assistant-said-end-call-phrase':
            case 'success':
                return (
                    <div className="p-2 bg-[#10B9810D] bg-opacity-5 rounded-lg text-xs text-[#10B981] block xl:inline capitalize text-center xl:text-left">
                        {reason === 'assistant-said-end-call-phrase'
                            ? 'Customer Ended Call'
                            : reason}
                    </div>
                );
            case 'transfer':
            case 'transferred':
            case 'assistant-said-silence-time-out-phrase':
                return (
                    <div className="p-2 bg-[#F59E0B0D] bg-opacity-5 rounded-lg text-xs text-[#F59E0B] block xl:inline capitalize text-center xl:text-left">
                        {reason === 'assistant-said-silence-time-out-phrase'
                            ? ''
                            : 'Transferred'}
                    </div>
                );
            case 'fail':
                return (
                    <div className="p-2 bg-[#EF44440D] bg-opacity-5 rounded-lg text-xs text-[#EF4444] block xl:inline capitalize text-center xl:text-left">
                        {reason}
                    </div>
                );
            case 'abandonment':
                return (
                    <div className="p-2 bg-[#333] bg-opacity-5 rounded-lg text-xs text-[#333] block xl:inline capitalize text-center xl:text-left">
                        abandoned
                    </div>
                );
            default:
                return (
                    <div className="p-2 bg-[#333] bg-opacity-5 rounded-lg text-xs text-[#333] block xl:inline capitalize text-center xl:text-left">
                        {reason}
                    </div>
                );
        }
    };

    return (
        <>
            {!!show?.show && !!show.callId && (
                <CallLogModal
                    open={!!show.show && !!show.callId}
                    callId={show.callId || ''}
                    onClose={handleShow}
                />
            )}

            <div className="mt-8 mb-3">
                <h4 className="text-[#1C1C1C] text-lg md:text-xl font-semibold dark:text-primary-dark flex flex-wrap">
                    Drill to details{' '}
                    {showFilters ? (
                        <span className="font-normal ml-1">
                            {' '}
                            - Calls Analytics
                        </span>
                    ) : (
                        ''
                    )}
                    {!showFilters && (
                        <div className="ml-auto">
                            <button
                                onClick={() =>
                                    changeTab?.({
                                        value: 'detailed',
                                        name: 'Detailed View',
                                        element: DetailedTab,
                                    })
                                }
                                className="bg-gray-800 rounded-lg border-gray-200 dark:bg-[#00691F] text-white text-sm py-2 px-4 hover:bg-gray-800/80 flex items-center"
                                type="button">
                                Filter Table
                                <img
                                    alt="settings"
                                    className="ml-2"
                                    src="/svg/icons/settings.svg"
                                />
                            </button>
                        </div>
                    )}
                </h4>
                {showFilters && (
                    <form className="bg-gray-100 dark:bg-[#1F1F1F] rounded-2xl flex flex-wrap p-3 my-5">
                        <div className="w-48 mr-2">
                            <Select
                                name="Status"
                                register={register('finalStatus')}
                                containerStyle="w-full"
                                options={[
                                    { label: 'All', value: '' },
                                    ...statusOptions,
                                ]}
                            />
                        </div>
                        <div className="w-48 mx-2">
                            <Select
                                name="Reason"
                                register={register('endedReason')}
                                containerStyle="w-full"
                                options={[
                                    { label: 'All Reasons', value: '' },
                                    ...reasonsOptions,
                                ]}
                            />
                        </div>
                        <div className="w-80 mx-2 my-1">
                            <p className=" ml-1 text-[#414651] dark:text-white text-sm">
                                Search
                            </p>
                            <Controller
                                name="search"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <Input
                                        placeholder={
                                            'by Customer Phone number or Name'
                                        }
                                        containerStyle="mb-0 relative dark:bg-bgColor-darkMode"
                                        inputStyle={`mb-0 border focus:outline-none dark:bg-black dark:text-primary-dark text-sm dark:bg-[#333333] dark:border-transparent`}
                                        icon={'/assets/shared/search.svg'}
                                        showValue={true}
                                        inputValue={value}
                                        inputUniqueName={'search'}
                                        onChangeInput={onChange}
                                        hideInputSpace={true}
                                    />
                                )}
                            />
                        </div>
                        <div className="w-80 mx-2">
                            <Controller
                                name="transactionDateRange"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <DateRangePopover
                                        label="Date Range"
                                        value={value}
                                        onChange={onChange}
                                    />
                                )}
                            />
                        </div>
                        <div className="mt-6">
                            <button
                                className="flex flex-row justify-center items-center bg-white h-9 py-2 px-4 rounded-xl my-auto ml-5 hover:bg-white/50 dark:bg-[#333333] dark:text-[#00691F]"
                                onClick={handleReset}
                                type="button">
                                <img
                                    alt="reset"
                                    className="mr-2 dark:invert-0"
                                    width={20}
                                    src="/svg/icons/reset.svg"
                                />
                                Reset
                            </button>
                        </div>
                    </form>
                )}
                <div className="w-full my-3">
                    <div className="flex flex-wrap items-center no-print">
                        {['all', 'transfer', 'success', 'fail', 'abandoned'].map(
                            (details) => (
                                <button
                                    key={details}
                                    className={classNames(
                                        'text-sm font-medium p-2 border-b capitalize',
                                        tabs === details
                                            ? 'text-black border-primary-default dark:border-[#00691F] dark:text-[#00691F]'
                                            : 'text-gray-500 dark:text-white border-transparent',
                                    )}
                                    onClick={() =>
                                        handleTabs(
                                            details as
                                            | 'all'
                                            | 'transfer'
                                            | 'success'
                                            | 'fail'
                                            | 'abandoned',
                                        )
                                    }
                                    type="button">
                                    {details === 'transfer' ? 'Transferred' : details} (
                                    {details === 'all'
                                        ? calls?.data.totalByStatus
                                            .map((status) => status.count)
                                            .reduce(
                                                (acc, curr) => acc + curr,
                                                0,
                                            )
                                        : details === 'abandoned'
                                            ? calls?.data.totalByStatus.find(
                                                (status) =>
                                                    status._id === 'abandonment',
                                            )?.count
                                            : details === 'transfer'
                                                ? calls?.data.totalByStatus.find(
                                                    (status) =>
                                                        status._id === 'transfer' ||
                                                        status._id === 'transferred',
                                                )?.count
                                                : calls?.data.totalByStatus.find(
                                                    (status) =>
                                                        status._id === details,
                                                )?.count}
                                    )
                                </button>
                            ),
                        )}
                    </div>
                    <div className="mt-5">
                        <div className="w-full">
                            <div className="h-full border border-gray-200 dark:bg-[#1B1B1B] dark:border-transparent rounded-2xl">
                                {
                                    <div className="p-2 dark:text-primary-dark py-2 px-4">
                                        <Table
                                            columns={COLUMNS_DETAILS}
                                            paginationData={
                                                Array.isArray(
                                                    calls?.data.calls?.elements,
                                                ) &&
                                                    calls?.data.calls.elements
                                                        .length
                                                    ? pagination
                                                    : undefined
                                            }
                                            setPaginationState={setPagination}
                                            headStyle="text-gray-500 text-sm font-medium p-2 bg-white">
                                            {Array.isArray(
                                                calls?.data.calls.elements,
                                            ) &&
                                                calls?.data.calls.elements.map(
                                                    (call) => (
                                                        <tr
                                                            key={call._id}
                                                            className="dark:text-primary-dark">
                                                            <td className="px-6 py-2">
                                                                {call.callId}
                                                            </td>
                                                            <td className="px-6 py-2">
                                                                {
                                                                    call
                                                                        .otherData
                                                                        .customerPhone
                                                                }
                                                            </td>
                                                            <td className="px-6 py-2">
                                                                {formatStatus(
                                                                    call
                                                                        .otherData
                                                                        .endedReason,
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-2">
                                                                {formatStatus(
                                                                    call
                                                                        .otherData
                                                                        .finalStatus,
                                                                )}
                                                            </td>
                                                            <td className="px-6 py-2">
                                                                <button
                                                                    onClick={() =>
                                                                        handleShow(
                                                                            call._id,
                                                                        )
                                                                    }
                                                                    type="button">
                                                                    <img
                                                                        alt="view"
                                                                        className="rotate-180 dark:contrast-0"
                                                                        src="/assets/insights/see-more-icon.svg"
                                                                    />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ),
                                                )}
                                        </Table>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Call Management Section */}
            <div className="mt-8">
                <CallManagement />
            </div>
        </>
    );
};

export default DrillDetails;
